import CategoryIcon from '@mui/icons-material/Category';
import FormatShapesIcon from '@mui/icons-material/FormatShapes';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import PaletteIcon from '@mui/icons-material/Palette';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import { List, ListItem, Box, Typography, IconButton } from '@mui/material';
import { fabric } from 'fabric';
import { Canvas } from 'fabric/fabric-impl';
import { ChangeEvent, Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import ColorPaletteModule from './modules/ColorPaletteModule';
import ImageModule from './modules/ImageModule';
import ShapesModule from './modules/ShapesModule';
import TemplateModule from './modules/TemplateModule';
import { getNextInsertPosition } from '../utils/insertPosition';
import { ColorPalette } from '../types';

const ToolsSidebar = ({
  canvas,
  canvasHeight,
  canvasWidth,
  colorPalettes,
  setColorPalettes,
}: {
  canvas: MutableRefObject<Canvas | undefined>;
  canvasHeight: number;
  canvasWidth: number;
  colorPalettes: ColorPalette[];
  setColorPalettes: Dispatch<SetStateAction<ColorPalette[]>>;
}) => {
  const activeBrandColors =
    colorPalettes.find((palette) => Array.isArray(palette?.colors) && palette.colors.length > 0)?.colors || [];

  const getBackgroundObject = () =>
    canvas.current
      ?.getObjects()
      .find(
        (object: any) =>
          object?.isBackgroundLayer === true ||
          (object?.type === 'rect' && object?.selectable === false && object?.evented === false)
      );

  const addObjectAboveBackground = (object: fabric.Object) => {
    const targetCanvas = canvas.current;
    if (!targetCanvas) return;

    targetCanvas.add(object);
    targetCanvas.bringToFront(object);
    object.setCoords();
    targetCanvas.setActiveObject(object);
    targetCanvas.renderAll();
  };

  const [shapesInView, setShapesInView] = useState<boolean>(false);
  const [imageInView, setImageInView] = useState<boolean>(false);
  const [colorPalleteInView, setColorPalleteInView] = useState<boolean>(false);
  const [activeColor, setActiveColor] = useState<string>('#eeeeee');
  const [templateInView, setTemplateInView] = useState<boolean>(false);
  const [activeTools, setActiveTools] = useState<boolean[]>([false, false, false, false]);
  // refs
  const paletteBtnRef = useRef<HTMLLIElement | null>(null);
  const shapesBtnRef = useRef<HTMLLIElement | null>(null);
  const shapesPanelRef = useRef<HTMLDivElement | null>(null);

  // Handling the shapes button click
  const handleShapeBtnClick = () => {
    setShapesInView(!shapesInView);
  };

  const handleTextBtnClick = () => {
    const nextPosition = getNextInsertPosition(canvas.current, { step: 15, cascadeCount: 28 });
    const text = new fabric.Textbox('text', {
      top: nextPosition.top,
      left: nextPosition.left,
      editable: true,
      fontSize: 128,
      fill: '#000000',
    });
    text.setControlsVisibility({
      mt: false, // Middle top
      mb: false, // Middle bottom
      ml: true, // Middle left
      mr: true, // Middle right
      tl: false, // Top left
      tr: false, // Top right
      bl: false, // Bottom left
      br: false, // Bottom right
    });
    addObjectAboveBackground(text);
  };

  const handleImageInViewClick = () => {
    setShapesInView(false);
    setColorPalleteInView(false);
    setTemplateInView(false);
    setImageInView(!imageInView);
  };

  const handlePaletteClick = () => {
    setColorPalleteInView(!colorPalleteInView);
  };

  const handleBackgroundColorChange = (e: ChangeEvent<HTMLInputElement>) => {
    setActiveColor(e.target.value);
    const backgroundObject = getBackgroundObject();
    backgroundObject?.set('fill', e.target.value);
    canvas.current?.setBackgroundColor(e.target.value, () => {
      canvas.current?.renderAll();
    });
  };

  const handleTemplateClick = () => {
    setShapesInView(false);
    setColorPalleteInView(false);
    setImageInView(false);
    setTemplateInView(!templateInView);
  };

  const handleActiveToolsChange = (index: number) => {
    const activeToolsClone = [...activeTools];
    activeToolsClone[index] = !activeTools[index];
    setActiveTools(activeToolsClone);
  };

  const resetAll = () => {
    setShapesInView(false);
    setImageInView(false);
    setColorPalleteInView(false);
    setTemplateInView(false);
  };

  // Close module popovers when canvas changes (add/select/reset).
  useEffect(() => {
    let unbind: (() => void) | undefined;
    let waitForCanvasTimer: ReturnType<typeof setInterval> | undefined;

    const bindListeners = (targetCanvas: Canvas) => {
      const onObjectAdded = () => resetAll();
      const onSelectionCreated = () => resetAll();
      const onFocusReset = () => resetAll();

      targetCanvas.on('object:added', onObjectAdded);
      targetCanvas.on('selection:created', onSelectionCreated);
      targetCanvas.on('focus:reset', onFocusReset);

      return () => {
        targetCanvas.off('object:added', onObjectAdded);
        targetCanvas.off('selection:created', onSelectionCreated);
        targetCanvas.off('focus:reset', onFocusReset);
      };
    };

    if (canvas.current) {
      unbind = bindListeners(canvas.current);
    } else {
      waitForCanvasTimer = setInterval(() => {
        if (!canvas.current || unbind) return;
        unbind = bindListeners(canvas.current);
        if (waitForCanvasTimer) {
          clearInterval(waitForCanvasTimer);
          waitForCanvasTimer = undefined;
        }
      }, 100);
    }

    return () => {
      if (waitForCanvasTimer) clearInterval(waitForCanvasTimer);
      if (unbind) unbind();
    };
  }, [canvas]);

  // Close shapes popover when clicking outside its trigger and panel.
  useEffect(() => {
    if (!shapesInView) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (shapesBtnRef.current?.contains(target)) return;
      if (shapesPanelRef.current?.contains(target)) return;
      setShapesInView(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [shapesInView]);

  return (
    <>
      {/** Unified modern tile style for tool controls */}
      <List
        style={{
          height: '100%',
          background: 'linear-gradient(180deg, #23252a 0%, #1a1c20 100%)',
          boxSizing: 'border-box',
          padding: '8px 4px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 6,
          listStyle: 'none',
          borderRight: '1px solid #3c3f45',
        }}
      >
        {/** Background color */}
        <ListItem
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: '#2b2e34',
            border: '1px solid #3e424a',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.75,
            gap: 0.5,
            color: '#fff',
          }}
        >
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, letterSpacing: 0.3, color: '#cfd4dd' }}>
            BG
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              border: '1px solid #4f5560',
              overflow: 'hidden',
              backgroundColor: '#202228',
            }}
          >
            <input
              type='color'
              onChange={handleBackgroundColorChange}
              value={activeColor}
              aria-label='Background color'
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: 0, backgroundColor: 'transparent', cursor: 'pointer' }}
            ></input>
          </Box>
        </ListItem>

        {/** Shapes */}
        <ListItem
          ref={shapesBtnRef}
          onClick={() => handleActiveToolsChange(0)}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: '#2b2e34',
            border: activeTools[0] ? '1px solid #ff6961' : '1px solid #3e424a',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.7,
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#343942' },
          }}
        >
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, letterSpacing: 0.3, color: '#cfd4dd' }}>Shapes</Typography>
          <IconButton
            size='small'
            onClick={handleShapeBtnClick}
            title='shapes'
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              color: '#fff',
              background: activeTools[0] ? 'linear-gradient(135deg, #ff6961 0%, #ff8b57 100%)' : '#1f2126',
              border: activeTools[0] ? '1px solid #ff9e7f' : '1px solid #434952',
              '&:hover': {
                background: activeTools[0] ? 'linear-gradient(135deg, #ff736b 0%, #ff9565 100%)' : '#2a2d33',
              },
            }}
          >
            <CategoryIcon />
          </IconButton>
        </ListItem>

        {/** Text */}
        <ListItem
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: '#2b2e34',
            border: '1px solid #3e424a',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.7,
            color: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#343942' },
          }}
        >
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, letterSpacing: 0.3, color: '#cfd4dd' }}>Text</Typography>
          <IconButton
            size='small'
            onClick={handleTextBtnClick}
            title='text'
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              color: '#fff',
              background: '#1f2126',
              border: '1px solid #434952',
              '&:hover': { background: '#2a2d33' },
            }}
          >
            <FormatShapesIcon />
          </IconButton>
        </ListItem>

        {/** Assets */}
        <ListItem
          onClick={() => handleActiveToolsChange(1)}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: '#2b2e34',
            border: activeTools[1] ? '1px solid #ff6961' : '1px solid #3e424a',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.7,
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#343942' },
          }}
        >
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, letterSpacing: 0.3, color: '#cfd4dd' }}>Assets</Typography>
          <IconButton
            size='small'
            onClick={handleImageInViewClick}
            title='media'
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              color: '#fff',
              background: activeTools[1] ? 'linear-gradient(135deg, #ff6961 0%, #ff8b57 100%)' : '#1f2126',
              border: activeTools[1] ? '1px solid #ff9e7f' : '1px solid #434952',
              '&:hover': {
                background: activeTools[1] ? 'linear-gradient(135deg, #ff736b 0%, #ff9565 100%)' : '#2a2d33',
              },
            }}
          >
            <InsertPhotoOutlinedIcon />
          </IconButton>
        </ListItem>

        {/** Template */}
        <ListItem
          ref={paletteBtnRef}
          onClick={() => handleActiveToolsChange(2)}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: '#2b2e34',
            border: activeTools[2] ? '1px solid #ff6961' : '1px solid #3e424a',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.7,
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#343942' },
          }}
        >
          <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, letterSpacing: 0.3, color: '#cfd4dd' }}>Template</Typography>
          <IconButton
            size='small'
            onClick={handleTemplateClick}
            title='templates'
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              color: '#fff',
              background: activeTools[2] ? 'linear-gradient(135deg, #ff6961 0%, #ff8b57 100%)' : '#1f2126',
              border: activeTools[2] ? '1px solid #ff9e7f' : '1px solid #434952',
              '&:hover': {
                background: activeTools[2] ? 'linear-gradient(135deg, #ff736b 0%, #ff9565 100%)' : '#2a2d33',
              },
            }}
          >
            <ViewQuiltIcon />
          </IconButton>
        </ListItem>
        {/* <ListItem>
          <p>Load</p>
          <ToolButton handleClick={() => alert('edit tool coming soon')} title={'Load and Edit Media'}>
            <EditIcon />
          </ToolButton>
        </ListItem> */}
        <ListItem
          onClick={() => handleActiveToolsChange(3)}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: '#2b2e34',
            border: activeTools[3] ? '1px solid #ff6961' : '1px solid #3e424a',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.7,
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#343942' },
          }}
        >
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: 0.3, color: '#cfd4dd', textAlign: 'center', lineHeight: 1.1 }}>
            Brand Colors
          </Typography>
          <IconButton
            size='small'
            onClick={handlePaletteClick}
            title='color palette'
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              color: '#fff',
              background: activeTools[3] ? 'linear-gradient(135deg, #ff6961 0%, #ff8b57 100%)' : '#1f2126',
              border: activeTools[3] ? '1px solid #ff9e7f' : '1px solid #434952',
              '&:hover': {
                background: activeTools[3] ? 'linear-gradient(135deg, #ff736b 0%, #ff9565 100%)' : '#2a2d33',
              },
            }}
          >
            <PaletteIcon />
          </IconButton>
        </ListItem>
      </List>
      {shapesInView && (
        <div ref={shapesPanelRef}>
          <ShapesModule canvas={canvas} brandColors={activeBrandColors} />
        </div>
      )}
      {imageInView && <ImageModule canvas={canvas} handleClose={handleImageInViewClick} />}
      {colorPalleteInView && (
        <ColorPaletteModule
          paletteRef={paletteBtnRef}
          canvas={canvas}
          colorPalettes={colorPalettes}
          setColorPalettes={setColorPalettes}
        />
      )}
      {templateInView && (
        <TemplateModule
          canvas={canvas}
          handleClose={handleTemplateClick}
          height={canvasHeight}
          width={canvasWidth}
          bgColorUpdate={setActiveColor}
        />
      )}
    </>
  );
};

export default ToolsSidebar;
